import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { imageUploadService } from '../../services/imageUploadService';

interface ScreenshotUploadZoneProps {
  projectId: string;
  screenshots: string[];
  onScreenshotsChange: (screenshots: string[]) => void;
  maxScreenshots?: number;
  minScreenshots?: number;
  disabled?: boolean;
}

export default function ScreenshotUploadZone({
  projectId,
  screenshots,
  onScreenshotsChange,
  maxScreenshots = 10,
  minScreenshots = 0,
  disabled = false
}: ScreenshotUploadZoneProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const [previewScreenshot, setPreviewScreenshot] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 4000);
  };

  const validateFile = async (file: File): Promise<boolean> => {
    // Check file type
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      showError(`${file.name}: Only PNG and JPG files are allowed`);
      return false;
    }

    // Check file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showError(`${file.name}: File size exceeds 5MB limit`);
      return false;
    }

    // Check image dimensions
    try {
      const dimensions = await imageUploadService.getImageDimensions(file);
      if (dimensions.width < 480 || dimensions.height < 640) {
        showError(`${file.name}: Minimum dimensions are 480x640 (current: ${dimensions.width}x${dimensions.height})`);
        return false;
      }
    } catch (err) {
      showError(`${file.name}: Failed to read image dimensions`);
      return false;
    }

    return true;
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);
    const remaining = maxScreenshots - screenshots.length;

    if (filesArray.length > remaining) {
      showError(`Can only add ${remaining} more screenshot(s). Maximum is ${maxScreenshots}.`);
      return;
    }

    setUploading(true);
    const previewUrls: string[] = [];

    for (let i = 0; i < filesArray.length; i++) {
      const file = filesArray[i];
      const fileKey = `${file.name}-${Date.now()}-${i}`;

      // Validate file
      const isValid = await validateFile(file);
      if (!isValid) continue;

      try {
        // Generate and show preview while uploading
        const preview = imageUploadService.generatePreviewUrl(file);
        previewUrls.push(preview);
        
        // Add preview to screenshots immediately
        onScreenshotsChange([...screenshots, ...previewUrls]);

        // Update progress
        setUploadProgress(prev => ({ ...prev, [fileKey]: 0 }));

        // Upload with moderation
        const result = await imageUploadService.uploadImage(
          file,
          projectId,
          'screenshot',
          (progress) => {
            setUploadProgress(prev => ({ ...prev, [fileKey]: progress }));
          }
        );

        // Clean up preview URL
        imageUploadService.cleanupPreviewUrl(preview);

        // Replace preview with final URL
        onScreenshotsChange(current => 
          current.map(url => url === preview ? result.url : url)
        );

        console.log(`✅ Uploaded screenshot: ${result.url}`);

        // Clear progress for this file
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileKey];
          return newProgress;
        });
      } catch (err) {
        console.error('Failed to upload screenshot:', err);
        const errorMessage = err instanceof Error ? err.message : `Failed to upload ${file.name}`;
        
        // REMOVE preview if moderation failed
        const preview = previewUrls[previewUrls.length - 1];
        if (preview) {
          imageUploadService.cleanupPreviewUrl(preview);
          onScreenshotsChange(current => current.filter(url => url !== preview));
        }
        
        // Show specific error message (includes moderation failures)
        showError(errorMessage);
        
        // Clear progress for failed file
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileKey];
          return newProgress;
        });
      }
    }

    setUploading(false);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteScreenshot = async (index: number) => {
    try {
      setDeletingIndex(index);
      const url = screenshots[index];

      // Wait a bit to show the loader
      await new Promise(resolve => setTimeout(resolve, 300));

      // Delete from Firebase
      await imageUploadService.deleteImage(url);

      // Update local state
      const updated = screenshots.filter((_, i) => i !== index);
      onScreenshotsChange(updated);

      console.log(`✅ Deleted screenshot: ${url}`);
    } catch (err) {
      console.error('Failed to delete screenshot:', err);
      showError('Failed to delete screenshot');
    } finally {
      setDeletingIndex(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !uploading) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || uploading) return;

    const files = e.dataTransfer.files;
    handleFiles(files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const handleBrowseClick = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  const isMaxReached = screenshots.length >= maxScreenshots;
  const uploadProgressEntries = Object.entries(uploadProgress);

  return (
    <div className="space-y-3">
      {/* Upload Zone */}
      {!isMaxReached && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleBrowseClick}
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all ${
            dragActive
              ? 'border-blue-500 bg-blue-500 bg-opacity-10'
              : disabled || uploading
              ? 'border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-50'
              : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-750 cursor-pointer'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleFileInputChange}
            disabled={disabled || uploading}
            className="hidden"
          />

          <div className="flex flex-col items-center gap-3">
            <div className={`p-3 rounded-full ${dragActive ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {uploading ? (
                <div className="w-8 h-8 border-3 border-gray-600 dark:border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Upload className={`w-8 h-8 ${dragActive ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
              )}
            </div>

            <div>
              <p className={`text-sm font-medium ${dragActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                {uploading ? 'Uploading...' : dragActive ? 'Drop files here' : 'Drop screenshots here or click to browse'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                PNG or JPG • Min 480x640 • Max 5MB • Up to {maxScreenshots} screenshots
              </p>
            </div>

            {uploadProgressEntries.length > 0 && (
              <div className="w-full max-w-xs space-y-2 mt-2">
                {uploadProgressEntries.map(([key, progress]) => (
                  <div key={key} className="space-y-1">
                    <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Screenshots Grid */}
      {screenshots.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              {screenshots.length} of {maxScreenshots} uploaded
            </span>
            {isMaxReached && (
              <span className="text-yellow-600 dark:text-yellow-500">Maximum reached</span>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <AnimatePresence mode="popLayout">
              {screenshots.map((url, index) => (
                <motion.div
                  key={url}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
                  transition={{ layout: { duration: 0.3, ease: "easeInOut" } }}
                  className={`relative aspect-[9/16] rounded-lg border-2 overflow-hidden ${
                    deletingIndex === index
                      ? 'border-gray-300 dark:border-gray-700 opacity-50'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
                >
                  {/* Screenshot image - click to preview */}
                  <button
                    type="button"
                    onClick={() => setPreviewScreenshot(url)}
                    disabled={deletingIndex === index}
                    className="w-full h-full"
                  >
                    <img
                      src={url}
                      alt={`Screenshot ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>

                  {/* Deleting loader overlay */}
                  {deletingIndex === index && (
                    <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
                        <span className="text-white text-xs font-medium">Deleting...</span>
                      </div>
                    </div>
                  )}

                  {/* Delete button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteScreenshot(index);
                    }}
                    disabled={deletingIndex === index || disabled}
                    className={`absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all ${
                      deletingIndex === index ? 'opacity-0' : 'opacity-100'
                    }`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>

                  {/* Screenshot number indicator */}
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black bg-opacity-60 rounded text-xs text-white font-medium">
                    #{index + 1}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Empty State */}
      {screenshots.length === 0 && !uploading && (
        <div className="text-center py-6 text-gray-500 dark:text-gray-500 text-sm flex flex-col items-center gap-2">
          <ImageIcon className="w-12 h-12 text-gray-400 dark:text-gray-600" />
          <p>No screenshots uploaded yet</p>
          <p className="text-xs text-gray-500 dark:text-gray-600">Upload screenshots to showcase your game</p>
        </div>
      )}

      {/* Screenshot Preview Modal */}
      {previewScreenshot && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
          onClick={() => setPreviewScreenshot(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] p-4">
            <button
              onClick={() => setPreviewScreenshot(null)}
              className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={previewScreenshot}
              alt="Screenshot preview"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
              loading="eager"
              decoding="sync"
            />
          </div>
        </div>
      )}

      {/* Error Notification */}
      {error && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out max-w-2xl">
          <div className="bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}
    </div>
  );
}

